import React, { useImperativeHandle, forwardRef, useRef, useEffect, useState, memo } from "react";
import { getImageUrl } from "../../../commons/assets_path";
import { OutputDataArray } from "../../../types/types";

type Props = {
  initialContent: string;
  onContentChange?: (content: string) => void;
  outputs: OutputDataArray;
  handleFocus?: () => void;
  handleInput?: (value: string) => void;
  handleBlur?: (value: string) => void;
  handleClick?: () => void;
  name?: string;
  id?: string;
  errorClass?: string;
  style?: object;
  testId?: string;
};

export type TagValue = {
  icon_path?: string;
  display_name: string;
  test_value: string;
};

export type HandlerType = {
  target: React.RefObject<HTMLDivElement>;
  value: string;
  formatForBackend: string;
  handleInsertTag: (value: TagValue) => void;
};

const TagTextareaField = forwardRef<HandlerType, Props>(
  (
    {
      initialContent,
      outputs,
      handleFocus = null,
      handleInput = null,
      handleClick = null,
      onContentChange = null, // NOTE:不要な可能性があるので後に削除するかも
      handleBlur = null,
      name = "",
      id = "",
      errorClass = "",
      style = {},
      testId = "",
    },
    ref,
  ) => {
    const [value, setValue] = useState<string>("");
    const [isRefReady, setIsRefReady] = useState(false);
    const localRef = useRef<HTMLDivElement>(null);
    const savedRangeRef = useRef<Range | null>(null);
    const [saveRange, setSaveRange] = useState<Range | null>(null);

    useImperativeHandle(ref, () => ({
      target: localRef,
      formatForBackend: value,
      handleInsertTag,
      value,
    }));

    // キャレット位置を保存
    const saveCaretPosition = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        savedRangeRef.current = selection.getRangeAt(0);
        setSaveRange(selection.getRangeAt(0));
      }
    };

    // キャレット位置を復元
    const restoreCaretPosition = () => {
      const selection = window.getSelection();
      if (selection && savedRangeRef.current) {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
      }
    };

    const isLineBlankDiv = (node: Node | null) => {
      return node ? node.nodeName === "DIV" && (node as HTMLElement).attributes.length === 0 : false;
    };

    const handleInputChange = () => {
      const editor = localRef.current;
      if (!editor) return;

      const selection = window.getSelection();
      if (!selection) return;
      // 文字削除時、editor内がbrのみになった時発火
      if (!editor?.textContent && editor?.childNodes.length && editor?.childNodes[0].nodeName === "BR") {
        const wrapper = document.createElement("div");
        wrapper.appendChild(editor.childNodes[0]);
        editor.prepend(wrapper);
      }

      // 最初のtextNode１文字入力時に発火
      if (
        editor?.textContent?.length === 1 &&
        editor?.childNodes.length &&
        editor.childNodes[0].nodeType === Node.TEXT_NODE
      ) {
        const wrapper = document.createElement("div");
        wrapper.appendChild(editor.childNodes[0]);
        editor.prepend(wrapper);
        const divNode = editor.childNodes[0];
        const range = document.createRange();
        range.setStart(divNode, 1);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      if (editor) {
        const content = editor.innerHTML;

        if (onContentChange) {
          onContentChange(content);
        }
        setValue(formatForBackend());
        if (handleInput) {
          handleInput(formatForBackend());
        }
      }
      saveCaretPosition();
    };

    const handleTagClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.className === "removeTag") {
        target.closest(".react-tag-input__tag__outer")?.remove();
        handleInputChange(); // 削除後の内容を更新
      } else {
        // タグがクリックされた場合、キャレットをタグの後ろに移動
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const clickedTag = target.closest(".react-tag-input__tag__outer");
          if (clickedTag) {
            range.setStartAfter(clickedTag);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    };

    // NOTE: HTMLエンティティの除去
    const decodeHTMLEntities = (text: string) => {
      const html = text
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/div>/gi, "\n")
        .replace(/<div>/gi, "")
        .replace(/\n+$/, "");

      // HTMLタグをすべて除去
      const temp = document.createElement("textarea");
      temp.innerHTML = html;
      const result = temp.value || "";
      // 不要な空白をトリミングして返す
      return result;
    };

    // NOTE: BEに送るために{{output}}に変換する
    const formatForBackend = () => {
      if (!localRef?.current) return "";

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = localRef.current.innerHTML;
      tempDiv.innerHTML = tempDiv.innerHTML.replace(/<br\s*\/?>/g, "");

      // タグを探して{{}}形式に変換
      const tags = tempDiv.querySelectorAll(".react-tag-input__tag__outer");
      tags.forEach((tag) => {
        const tagContent = tag.querySelector(".react-tag-input__tag__content");

        if (tagContent instanceof HTMLElement) {
          // タグを{{タグ名}}形式に置換
          const displayName = tagContent.innerText;
          tag.replaceWith(`{{${displayName}}}`);
        }
      });
      tempDiv.innerHTML = tempDiv.innerHTML.replace(/\s*(<[^>]+>)\s*/g, "$1").trim();
      // {{}}内の空白や改行を削除
      tempDiv.innerHTML = tempDiv.innerHTML.replace(/{{\s*(.*?)\s*}}/g, (match, p1) => {
        return `{{${p1.trim()}}}`;
      });

      // contentEditable デフォルト機能でspanが入るので対応
      const spanNodes = tempDiv.querySelectorAll("span");
      spanNodes.forEach((node) => {
        const text = node.textContent;
        if (text) {
          node.replaceWith(text);
        } else {
          node.remove();
        }
      });

      // NOTE明日の俺へ、"aa"入力して改行して"bb"入力して1文字目のbから削除して２文字目のbを1行目に移動させてタグ入力して改行するとバグる

      return decodeHTMLEntities(tempDiv.innerHTML);
    };

    // NOTE: タグHTMLを生成
    const tagFormat = (value: TagValue) => {
      const iconPath = value?.icon_path ? getImageUrl(value.icon_path) : "";
      const iconNode = iconPath ? `<img src="${iconPath}" alt="" width=20 height=20 />` : "";
      const removeIconPath = getImageUrl("images/icons/grayCancel.svg");
      const displayName = value.display_name.replace(/{{(.*?)}}/, "$1");
      const testValue = value?.test_value ? `<p class="react-tag-input__tag__desc">${value.test_value}</p>` : "";
      const htmlString = `
      <div class="react-tag-input__tag__outer" contentEditable="false" style="vertical-align: middle; display: inline-flex; padding: 0 5px 0 5px; margin-left: 3px">
        <div class="react-tag-input__tag" style="display: inline-flex; vertical-align: middle;">
          ${iconNode}
          <div class="react-tag-input__tag__content" style="padding: 0;">
            ${displayName}
          </div>
          ${testValue}
          <img src="${removeIconPath}" width=16 height=16 class="removeTag" style="cursor: pointer;" />
        </div>
      </div>
      `;

      return htmlString;
    };

    // 取得した値からタグを入力
    const handleInsertTag = (value: TagValue) => {
      restoreCaretPosition();
      const htmlString = tagFormat(value);
      const selection = window.getSelection();
      const editor = localRef.current;
      if (!selection || !editor) return;

      // カーソルがcontentEditable内にあるか判定
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) return;

      const template = document.createElement("template");
      template.innerHTML = htmlString.trim();

      const insertNode = template.content.childNodes[0];
      if (editor.innerHTML === "") {
        const wrapper = document.createElement("div");
        wrapper.appendChild(insertNode);
        range.insertNode(wrapper);
      } else if (
        range.startContainer === editor &&
        editor.childNodes.length === 1 &&
        isLineBlankDiv(editor.childNodes[0])
      ) {
        /*
          NOTE:タグ入力時にrange.startContainerがeditorになる時があるので
          上記対策で行div要素をstartContainerにする処理
        */
        range.setStart(editor.childNodes[0], 0);
        range.insertNode(insertNode);
      } else if (range.startContainer.parentNode?.nodeName === "SPAN") {
        /*
          NOTE:ブラウザ仕様でspanタグが挿入された時、spanタグ外にキャレットを移動する。
        */
        range.setStartAfter(range.startContainer.parentNode);
        range.insertNode(insertNode);
      } else {
        range.insertNode(insertNode);
      }
      range.setStartAfter(insertNode);
      range.collapse(true);

      selection.removeAllRanges();
      selection.addRange(range);

      // 挿入後の入力内容を更新
      handleInputChange();
    };

    // NOTE: propsで受け取った値から取得した値の箇所をタグにリプレイスする
    const replaceTemplateTags = (input: string) => {
      if (!input) return "";

      // 改行文字 `/n` で分割し、それぞれを<div>でラップ
      const wrappedWithDivs = input
        .split("\n")
        .map((line) => `<div>${line.trim() || "</br>"}</div>`)
        .join("");

      // {{}}を探して置換
      const replacedText = wrappedWithDivs.replace(/{{(.*?)}}/g, (match, p1) => {
        const output = findOutputByDisplayName(p1);
        return output ? tagFormat(output as TagValue).trim() : match; // 対象外は元のテキストをそのまま保持
      });

      return replacedText;
    };
    // NOTE: タグへのリプレイスに必要な値をoutputsから取得
    const findOutputByDisplayName = (targetDisplayName: string) => {
      for (const item of outputs) {
        const match = item.outputs.find((output) => output.display_name === targetDisplayName);

        if (match) {
          return {
            display_name: match.display_name,
            test_value: match.test_value,
            icon_path: item.icon_path,
          };
        }
      }
      return {
        display_name: targetDisplayName,
      };
    };

    // NOTE: コピー元のフォントをリセットする
    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault(); // デフォルトのペースト動作を無効化
      const editor = localRef.current;
      if (!editor) return;
      // クリップボードからプレーンテキストを取得
      const text = e.clipboardData.getData("text/plain");
      const textareaNode = document.createElement("textarea");
      const fragment = document.createDocumentFragment();
      textareaNode.innerHTML = text;
      const copyData = textareaNode.value.split("\n");

      // コピー対象が複数行の場合
      if (copyData.length > 1) {
        copyData.forEach((textNode) => {
          const wrapper = document.createElement("div");

          // tentNodeが空文字列の時は<br />を入れる
          if (!textNode) {
            const br = document.createElement("br");
            wrapper.appendChild(br);
          } else {
            wrapper.innerText = textNode;
          }
          fragment.appendChild(wrapper);
        });
        // キャレット位置を取得してテキストを挿入
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && fragment.childNodes.length > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents(); // 現在の選択範囲を削除
          const lastChild = fragment.lastChild;

          // 同じ行に入力途中の文字列がある場合
          if (range.startContainer.nodeType === Node.TEXT_NODE) {
            const currentContainer = range.startContainer.parentNode;

            if (currentContainer) {
              editor.insertBefore(fragment, currentContainer.nextSibling);
            }
          } else if (!editor.textContent) {
            if (editor.childNodes[0]?.nodeName === "BR") {
              editor.childNodes[0].remove();
              editor.insertBefore(fragment, null);
            } else if (isLineBlankDiv(range.startContainer)) {
              const childNodes = editor.childNodes;
              if (childNodes) {
                const rowIndex = Array.from(editor.childNodes).indexOf(range.startContainer as ChildNode);

                localRef.current.replaceChild(fragment, editor.childNodes[rowIndex]);
              }
            } else {
              editor.insertBefore(fragment, null);
            }
          } else {
            const currentContainer = range.startContainer;
            // 連続でペーストした時に、currentContainerとeditorが同じ要素を参照して、エラーが出るため
            if (editor !== currentContainer && editor.contains(currentContainer)) {
              editor.insertBefore(fragment, currentContainer);
            }
          }
          // キャレットを挿入したテキストの後ろに移動
          if (lastChild && lastChild.nodeType === Node.ELEMENT_NODE) {
            range.setStartAfter(lastChild);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      } else {
        // コピー対象が1行の場合
        const textNode = document.createTextNode(copyData[0]);
        fragment.appendChild(textNode);

        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && fragment.childNodes.length > 0) {
          const range = selection.getRangeAt(0);
          const pasteNode = fragment.lastChild;
          range.deleteContents(); // 現在の選択範囲を削除
          range.insertNode(pasteNode!);
          range.setStartAfter(pasteNode!);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    };

    const handleBlur2 = () => {
      const editor = localRef.current;

      if (!editor) return;

      // 子ノードを走査
      const childNodes = Array.from(editor.childNodes);
      const targetNodes = childNodes.filter(
        (node) => node.nodeName !== "DIV" || (node as HTMLElement).attributes.length !== 0,
      );

      if (targetNodes.length) {
        const wrapper = document.createElement("div");
        targetNodes.forEach((node) => {
          wrapper.appendChild(node.cloneNode(true));
          node.remove();
        });
        localRef.current.prepend(wrapper);
      }
    };

    const handleTextFormattingShortcuts = (event: React.KeyboardEvent<HTMLDivElement>) => {
      const isBoldShortcut = (event.metaKey && event.key === "b") || (event.ctrlKey && event.key === "b");
      const isItalicShortcut = (event.metaKey && event.key === "i") || (event.ctrlKey && event.key === "i");
      const isUnderlineShortcut = (event.metaKey && event.key === "u") || (event.ctrlKey && event.key === "u");

      if (isBoldShortcut || isItalicShortcut || isUnderlineShortcut) {
        event.preventDefault(); // デフォルトのショートカット処理を無効化
      }
    };

    const getCurrentLineDiv = (range: Range) => {
      let element = range.startContainer;
      while (element) {
        if (isLineBlankDiv(element)) {
          return element;
        }
        element = element.parentNode as Node;
      }
    };

    const escapeSlackMentions = (str: string) => {
      return str.replace(/<!(subteam\^[^>|]+(?:\|[^>]+)?|here|channel|everyone)>/g, (match) => {
        return match.replace("<", "&lt;").replace(">", "&gt;");
      });
    };

    const escapeHtmlTags = (str: string) => {
      if (!str) return "";
      // {{タグ}}の中身はエスケープしないように正規表現で一時的に置き換える
      let tempStr = str;
      const tagPlaceholders: { placeholder: string; original: string }[] = [];

      // {{タグ}}を見つけて一時的なプレースホルダーに置き換える
      tempStr = tempStr.replace(/{{(.*?)}}/g, (match, p1) => {
        const placeholder = `__TAG_PLACEHOLDER_${tagPlaceholders.length}__`;
        tagPlaceholders.push({ placeholder, original: match });
        return placeholder;
      });

      // HTMLタグをエスケープ
      tempStr = tempStr.replace(/</g, "&lt;").replace(/>/g, "&gt;");

      // プレースホルダーを元の{{タグ}}に戻す
      tagPlaceholders.forEach(({ placeholder, original }) => {
        tempStr = tempStr.replace(placeholder, original);
      });

      return tempStr;
    };

    useEffect(() => {
      const editor = localRef.current;
      if (editor) {
        // 先にHTMLタグをエスケープしてから、replaceTemplateTagsを実行
        const escapedContent = escapeHtmlTags(initialContent);
        // replaceTemplateTagsの入力はescapedContentを使うように修正
        const replacedText = escapeSlackMentions(replaceTemplateTags(escapedContent));

        editor.innerHTML = replacedText;

        if (!editor.textContent) {
          editor.innerHTML = "<div><br /></div>";
        }
        setValue(initialContent);
      }
    }, []);

    useEffect(() => {
      if (localRef.current) {
        setIsRefReady(true); // 初回レンダリング後にrefの設定を確定
      }
    }, []);

    return (
      <>
        <input type="hidden" name={name} value={value} id={id} data-testid={`${testId}-value`} />
        <div
          ref={localRef}
          contentEditable
          className={`addressInput form-control no-scrollbar ${errorClass}`}
          data-testid={`${testId}-field`}
          onInput={() => handleInputChange()}
          onCompositionEnd={() => handleInputChange()}
          onClick={handleTagClick}
          onFocus={(e) => {
            if (handleFocus) {
              handleFocus();
            }
            restoreCaretPosition();
            setIsRefReady(true);
          }}
          onBlur={(e) => {
            handleBlur2();
            const result = formatForBackend();
            saveCaretPosition();
            setValue(result);
            if (handleBlur) {
              handleBlur(result);
            }
          }}
          onKeyDown={(e) => {
            const editor = localRef.current;
            if (!editor) return;
            if (e.key === "Enter" && e.shiftKey) {
              e.preventDefault();
            }
            handleTextFormattingShortcuts(e);
            if (e.key === "Backspace" || e.key === "Delete") {
              const selection = window.getSelection();

              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const startContainer = range.startContainer; // キャレットがあるノード
                const startOffset = range.startOffset; // キャレットのオフセット

                if (e.metaKey || e.ctrlKey) {
                  e.preventDefault();
                  const lineDiv = getCurrentLineDiv(range);
                  const offset = range.startOffset;
                  const length = startContainer.textContent?.length || 0;

                  if (startContainer.nodeType === Node.TEXT_NODE && startOffset !== length) {
                    range.setStart(startContainer, 0);
                    range.setEnd(startContainer, offset);
                    selection.removeAllRanges();
                    selection.addRange(range);
                    range.deleteContents();
                  } else if (lineDiv) {
                    const newDiv = document.createElement("div");
                    const br = document.createElement("br");
                    newDiv.appendChild(br);
                    editor.replaceChild(newDiv, lineDiv);
                    range.setStart(newDiv, 0);
                    range.collapse(true);
                  }

                  selection.removeAllRanges();
                  selection.addRange(range);
                } else if (startContainer.nodeType === Node.TEXT_NODE) {
                  // textNode削除&carret位置の右側にタグがある時、最後の一文字の削除だけ無効化して、手動で消す
                  // contentEditableのデフォルト機能で無駄なbrが入るため。
                  const textNode = startContainer;

                  // キャレットがテキストノードの末尾にある場合
                  if (startOffset === textNode.textContent?.length && textNode.textContent.length === 1) {
                    // テキストノードの次の兄弟ノードを取得
                    const isSpan = textNode.parentElement?.nodeName === "SPAN";
                    const parentElement = isSpan ? textNode.parentElement.parentElement : textNode.parentElement;
                    const childNodes = parentElement?.childNodes;

                    if (isLineBlankDiv(parentElement) && childNodes) {
                      const target = isSpan ? textNode.parentElement : textNode;
                      const textNodeIndex = Array.from(childNodes).indexOf(target as ChildNode);

                      // テキストノードの次のノードを確認
                      const nextNode = childNodes[textNodeIndex + 1];
                      if (
                        nextNode &&
                        nextNode.nodeType === Node.ELEMENT_NODE &&
                        (nextNode as HTMLElement).classList.contains("react-tag-input__tag__outer")
                      ) {
                        e.preventDefault();
                        if (isSpan) {
                          parentElement.removeChild(target);
                        } else {
                          textNode.textContent = "";
                        }
                      }
                    }
                  }
                }
              }
            }
          }}
          onPaste={handlePaste}
          style={{
            outline: "none",
            boxShadow: "none",
            overflow: "auto",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            maxHeight: "100%",
            height: "100%",
            lineHeight: "2.5",
            ...style,
          }}
        />
      </>
    );
  },
);

export default TagTextareaField;
